import { initComponentStore } from "@/utils/mobx";
import { appStore } from "@/app_store";
import { PhysicalRepositoryStore, ManagedRepositoryStore } from "@/app_store/agent";
import { ContextMenuStore } from "@/components/toolkit/ContextMenu/store";
import { t } from "@/i18n";

export class BranchSelectStore {
    showDialog: boolean = false;
    newBranchName: string = "";
    isDropdownOpen: boolean = false;
    dropdownTop: number = 0;
    dropdownLeft: number = 0;
    searchText: string = "";
    confirmBranch: string | null = null;
    resetDialogOpen: boolean = false;
    actionsMenuStore = new ContextMenuStore();

    get vaultSelected(): boolean { return appStore.state.vaultMode; }

    get isBusy(): boolean { return appStore.state.isRepoActionInProgress; }

    constructor() {
        initComponentStore(this);
    }

    get container() {
        return appStore.agent.session.container;
    }

    get isValidContainer(): boolean {
        const c = this.container;
        return c instanceof PhysicalRepositoryStore || c instanceof ManagedRepositoryStore;
    }

    get currentBranch(): string | undefined {
        const c = this.container;
        if (c instanceof PhysicalRepositoryStore) return c.workspace.name;
        if (c instanceof ManagedRepositoryStore) return c.workspace.name;
        return undefined;
    }

    get ahead(): number {
        const c = this.container;
        if (c instanceof PhysicalRepositoryStore) return c.workspace?.ahead ?? 0;
        if (c instanceof ManagedRepositoryStore) return c.workspace?.ahead ?? 0;
        return 0;
    }

    get behind(): number {
        const c = this.container;
        if (c instanceof PhysicalRepositoryStore) return c.workspace?.behind ?? 0;
        if (c instanceof ManagedRepositoryStore) return c.workspace?.behind ?? 0;
        return 0;
    }

    get allBranches(): string[] {
        const c = this.container;
        if (c instanceof PhysicalRepositoryStore) return c.branches;
        if (c instanceof ManagedRepositoryStore) return c.branches;
        const b = this.currentBranch;
        return b ? [b] : [];
    }

    get filteredBranches(): string[] {
        const q = this.searchText.toLowerCase().trim();
        if (!q) return this.allBranches;
        return this.allBranches.filter(b => b.toLowerCase().includes(q));
    }

    toggleDropdown(top: number, left: number) {
        if (this.isDropdownOpen) {
            this.closeDropdown();
        } else {
            this.dropdownTop = top;
            this.dropdownLeft = left;
            this.isDropdownOpen = true;
        }
    }

    closeDropdown() {
        this.isDropdownOpen = false;
        this.searchText = "";
    }

    changeSearchText(v: string) {
        this.searchText = v;
    }

    selectBranch(branch: string) {
        this.closeDropdown();
        appStore.state.vaultMode = false;
        if (branch === this.currentBranch) return;
        this.confirmBranch = branch;
    }

    cancelConfirm() {
        this.confirmBranch = null;
    }

    async confirmCheckout() {
        const branch = this.confirmBranch;
        if (!branch) return;
        this.confirmBranch = null;
        const c = this.container;
        if (c instanceof PhysicalRepositoryStore || c instanceof ManagedRepositoryStore) {
            await appStore.state.trackRepoAction(() => c.select_branch(branch));
        }
    }

    async confirmCheckoutCurrent() {
        const branch = this.currentBranch;
        if (!branch) return;
        this.confirmBranch = null;
        const c = this.container;
        if (c instanceof PhysicalRepositoryStore || c instanceof ManagedRepositoryStore) {
            await appStore.state.trackRepoAction(() => c.select_branch(branch));
        }
    }

    selectVault() {
        appStore.state.vaultMode = true;
        this.closeDropdown();
    }

    // Repo actions menu. Refresh/push/pull/reset run in ReviewSectionStore, which owns the
    // error / merge banners; branch actions reuse this component's dropdown and dialog.
    showActionsMenu(x: number, y: number, dropdownTop: number, dropdownLeft: number) {
        const items = [
            { title: t("review_action_refresh"), value: "refresh" },
            { title: t("review_action_push"), value: "push" },
            { title: t("review_action_pull"), value: "pull" },
            { title: t("review_action_reset"), value: "reset" },
            { title: t("review_switch_branch_title"), value: "select-branch" },
            { title: t("review_new_branch_dialog_title"), value: "create-branch" },
        ];
        this.actionsMenuStore.show(x, y, items, (value: string) => {
            if (value === "push" || value === "pull" || value === "refresh") appStore.state.requestRepoAction(value);
            else if (value === "reset") this.resetDialogOpen = true;
            else if (value === "select-branch") this.toggleDropdown(dropdownTop, dropdownLeft);
            else if (value === "create-branch") this.openDialog();
        });
    }

    confirmReset() {
        this.resetDialogOpen = false;
        appStore.state.requestRepoAction("reset");
    }

    cancelReset() {
        this.resetDialogOpen = false;
    }

    openDialog() {
        this.closeDropdown();
        this.showDialog = true;
        this.newBranchName = "";
    }

    closeDialog() {
        this.showDialog = false;
        this.newBranchName = "";
    }

    changeBranchName(v: string) {
        this.newBranchName = v;
    }

    async createBranch() {
        const name = this.newBranchName.trim();
        if (!name) return;
        await appStore.state.trackRepoAction(() => appStore.git.createBranch(name));
        this.closeDialog();
    }
}
